<template>
  <article :class="['v2-credential', { 'v2-credential--expired': isExpired }]">
    <div class="v2-credential__top">
      <div class="v2-credential__avatar" aria-hidden="true">
        <img
          v-if="logoSrc"
          :src="logoSrc"
          :alt="companyName"
          class="v2-credential__avatar-img"
        />
        <span v-else class="v2-credential__avatar-initials">{{ initials }}</span>
      </div>

      <span v-if="statusBadge" :class="['v2-credential__status', `v2-credential__status--${statusBadge.variant}`]">
        <v-icon size="14">{{ statusBadge.icon }}</v-icon>
        {{ statusBadge.label }}
      </span>
    </div>

    <div class="v2-credential__identity">
      <h2 class="v2-credential__name v2-credential__name--primary">{{ primaryName }}</h2>
      <p v-if="secondaryName" class="v2-credential__role">{{ secondaryName }}</p>
    </div>

    <div class="v2-credential__qr">
      <div class="v2-credential__qr-frame">
        <img
          v-if="qrSrc && !locked"
          :src="qrSrc"
          :alt="`Código QR de ${displayName}`"
        />
        <v-icon v-else-if="locked" size="40" color="error">mdi-lock</v-icon>
        <v-icon v-else size="28" color="grey">mdi-qrcode</v-icon>
      </div>
    </div>

    <div class="v2-credential__footer">
      <div class="v2-credential__footer-data">
        <div class="v2-credential__footer-item">
          <p class="v2-credential__label">Tipo de membresía</p>
          <p class="v2-credential__value">{{ membershipLabel }}</p>
        </div>
        <div class="v2-credential__footer-item">
          <p class="v2-credential__label">Vencimiento</p>
          <p :class="['v2-credential__value', expirationClass]">
            {{ expirationLabel }}
          </p>
        </div>
        <div class="v2-credential__footer-item">
          <p class="v2-credential__label">Capítulo</p>
          <p class="v2-credential__value">{{ branchLabel }}</p>
        </div>
      </div>

      <div class="v2-credential__member-of" aria-label="Miembro de">
        <p class="v2-credential__member-of-label">Miembro de</p>
        <div class="v2-credential__member-of-logo">
          <img
            v-if="headOfficeLogo"
            :src="headOfficeLogo"
            :alt="headOfficeName"
            class="v2-credential__member-of-img"
          />
          <span v-else class="v2-credential__member-of-initials">{{ headOfficeInitials }}</span>
        </div>
        <p v-if="headOfficeName" class="v2-credential__member-of-name">
          {{ headOfficeName }}
        </p>
      </div>
    </div>

    <span class="v2-credential__particles" aria-hidden="true" />
  </article>
</template>

<script>
import moment from "moment";
import { isOrganization } from "@/share/validateOrganization";

const TYPE_LABELS = {
  AS: "Asociado",
  MEMBER: "Asociado",
  AF: "Afiliado",
  MEMBERSHIP_USER: "Afiliado",
  IN: "Internacional",
};

const FORMAT_DATE = "DD/MM/YYYY";

const buildQrUrl = (data) => {
  const base = "https://api.qrserver.com/v1/create-qr-code/";
  const params = new URLSearchParams({
    data,
    bgcolor: "deeae0",
    size: "240x240",
  });
  return `${base}?${params.toString()}`;
};

export default {
  name: "MemberCredential",
  props: {
    user: { type: Object, default: null },
    credential: { type: Object, default: null },
    status: { type: String, default: null },
    locked: { type: Boolean, default: false },
  },
  computed: {
    isExpired() {
      return this.status === "EXPIRED" || this.status === "INACTIVO";
    },
    isProspect() {
      return this.status === "PROSPECTO" || this.status === "APPROVED";
    },
    isCompanyOrganization() {
      if (!this.user || !this.user.headOffice) return false;
      const headOffice = this.user.headOffice;
      const memberType = typeof headOffice === "object" ? headOffice.memberType : null;
      return memberType === "COMPANYS";
    },
    headOfficeId() {
      if (!this.user || !this.user.headOffice) return null;
      return typeof this.user.headOffice === "object"
        ? this.user.headOffice.id || this.user.headOffice._id
        : this.user.headOffice;
    },
    isAsofom() {
      return isOrganization("asofom", null, this.headOfficeId);
    },
    headOfficeObject() {
      if (!this.user || !this.user.headOffice) return null;
      return typeof this.user.headOffice === "object" ? this.user.headOffice : null;
    },
    credentialData() {
      return this.credential || {};
    },
    personName() {
      if (this.credentialData && this.credentialData.name) return this.credentialData.name;
      if (!this.user) return "";
      const fullName = [this.user.name, this.user.firstLastname, this.user.secondLastname]
        .filter(Boolean)
        .join(" ")
        .trim();
      return fullName;
    },
    companyName() {
      if (this.credentialData && this.credentialData.company && this.credentialData.company !== "N/D") {
        return this.credentialData.company;
      }
      if (this.user && this.user.company && this.user.company !== "N/D") {
        return this.user.company;
      }
      return "";
    },
    primaryName() {
      return this.isCompanyOrganization ? this.companyName || this.personName : this.personName || this.companyName;
    },
    secondaryName() {
      if (this.isCompanyOrganization) {
        return this.personName && this.personName !== this.primaryName ? this.personName : "";
      }
      return this.companyName && this.companyName !== this.primaryName ? this.companyName : "";
    },
    displayName() {
      return this.primaryName || this.personName || this.companyName || "N/D";
    },
    initials() {
      const name = this.displayName;
      return String(name)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";
    },
    logoSrc() {
      const head = this.user && this.user.headOffice;
      if (head && typeof head === "object") {
        if (head.logo && head.logo !== "N/D" && head.logo !== "NOT_ASSIGNED") return head.logo;
        if (head.image && head.image !== "N/D" && head.image !== "NOT_ASSIGNED") return head.image;
      }
      const dir = this.credentialData && this.credentialData.directoryData;
      if (dir && dir.logo && dir.logo !== "N/D" && dir.logo !== "NOT_ASSIGNED") return dir.logo;
      if (this.user && this.user.profilePicture && this.user.profilePicture !== "NOT_ASSIGNED" && this.user.profilePicture !== "N/D") {
        return this.user.profilePicture;
      }
      return null;
    },
    headOfficeName() {
      const head = this.headOfficeObject;
      if (!head) return "";
      return head.name || head.company || head.legalName || "";
    },
    headOfficeLogo() {
      const head = this.headOfficeObject;
      if (!head) return null;
      const candidate = head.profilePicture || head.logo || head.image || head.banner;
      if (!candidate || candidate === "N/D" || candidate === "NOT_ASSIGNED") return null;
      return candidate;
    },
    headOfficeInitials() {
      const name = this.headOfficeName || "WCh";
      return String(name)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";
    },
    membershipLabel() {
      if (this.credentialData && this.credentialData.membershipType) {
        return this.credentialData.membershipType;
      }
      if (this.user && this.user.memberInformation && this.user.memberInformation.type) {
        return this.user.memberInformation.type;
      }
      const externalId = this.credentialData && this.credentialData.externalId;
      const code = externalId ? String(externalId).substring(0, 2) : null;
      if (code && TYPE_LABELS[code]) return TYPE_LABELS[code];
      if (this.user && this.user.role && TYPE_LABELS[this.user.role]) {
        return TYPE_LABELS[this.user.role];
      }
      return "N/D";
    },
    branchLabel() {
      if (this.credentialData && this.credentialData.branch && this.credentialData.branch !== "N/D") {
        return this.credentialData.branch;
      }
      if (this.user && this.user.branch && this.user.branch !== "N/D") return this.user.branch;
      return "N/D";
    },
    expirationDateRaw() {
      const cred = this.credentialData;
      return (cred && (cred.inactiveDate || cred.expirationDate)) || null;
    },
    expirationLabel() {
      const d = this.expirationDateRaw;
      if (!d) return "N/D";
      const m = moment(d);
      return m.isValid() ? m.format(FORMAT_DATE) : "N/D";
    },
    expirationClass() {
      if (this.isExpired) return "v2-credential__value--error";
      if (this.isProspect) return "v2-credential__value--amber";
      return "";
    },
    statusBadge() {
      if (this.isExpired) {
        return { label: "Inactivo", variant: "error", icon: "mdi-alert-circle" };
      }
      if (this.isProspect) {
        return { label: "Por Vencer", variant: "warning", icon: "mdi-clock-outline" };
      }
      if (this.status === "ACTIVE") {
        return { label: "Activo", variant: "success", icon: "mdi-check-circle" };
      }
      return null;
    },
    credentialId() {
      if (this.credentialData && this.credentialData.id) return this.credentialData.id;
      if (this.credentialData && this.credentialData._id) return this.credentialData._id;
      if (this.user && this.user._id) return this.user._id;
      return null;
    },
    qrSrc() {
      if (this.locked) return null;
      const id = this.credentialId;
      if (!id) return null;
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const data = `${origin}/member/credential/${id}`;
      return buildQrUrl(data);
    },
  },
};
</script>

<style scoped>
.v2-credential {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  gap: var(--space-4);
  padding: var(--space-10) var(--space-8) var(--space-6);
  background: var(--gradient-chamber);
  color: var(--color-navy-on);
  border-radius: 1.25rem;
  box-shadow: var(--shadow-card);
  min-height: 100%;
  aspect-ratio: 9 / 16;
  max-width: 380px;
  margin-inline: auto;
}

@media (min-width: 1024px) {
  .v2-credential {
    max-width: none;
    margin-inline: 0;
  }
}

.v2-credential--expired {
  filter: grayscale(0.15);
}

.v2-credential__particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 12% 18%, rgba(255, 181, 0, 0.18) 0%, transparent 40%),
    radial-gradient(circle at 88% 80%, rgba(255, 181, 0, 0.08) 0%, transparent 35%);
  opacity: 0.6;
}

.v2-credential__top {
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.v2-credential__avatar {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  background-color: var(--color-surface);
  border: 3px solid var(--color-amber);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

@media (min-width: 768px) {
  .v2-credential__avatar {
    width: 96px;
    height: 96px;
  }
}

.v2-credential__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  padding: var(--space-1);
}

.v2-credential__avatar-initials {
  font-family: var(--font-headline);
  font-size: var(--fs-display-md);
  font-weight: var(--fw-bold);
  color: var(--color-navy-deep);
  line-height: 1;
}

.v2-credential__status {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  border-radius: var(--radius-full);
  line-height: 1;
  white-space: nowrap;
}

.v2-credential__status--success {
  background-color: var(--color-success);
  color: var(--color-success-on);
}

.v2-credential__status--warning {
  background-color: var(--color-warning);
  color: var(--color-warning-on);
}

.v2-credential__status--error {
  background-color: var(--color-error);
  color: var(--color-error-on);
}

.v2-credential__identity {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  align-items: center;
  min-width: 0;
  width: 100%;
}

.v2-credential__name--primary {
  font-family: var(--font-headline);
  font-size: var(--fs-display-sm);
  font-weight: var(--fw-bold);
  line-height: var(--lh-tight);
  color: var(--color-navy-on);
  margin: 0;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.v2-credential__role {
  font-family: var(--font-body);
  font-size: var(--fs-body-sm);
  color: var(--color-navy-on-soft);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.v2-credential__qr {
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  margin: var(--space-1) 0;
}

.v2-credential__qr-frame {
  width: min(140px, 100%);
  aspect-ratio: 1 / 1;
  background-color: var(--color-surface);
  padding: var(--space-2);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-sm);
}

@media (min-width: 768px) {
  .v2-credential__qr-frame {
    width: min(160px, 100%);
  }
}

.v2-credential__qr-frame img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.v2-credential__footer {
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.16);
}

.v2-credential__footer-data {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  text-align: left;
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

.v2-credential__footer-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.v2-credential__label {
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  color: var(--color-navy-on-soft);
  font-weight: var(--fw-semibold);
  margin: 0;
  line-height: 1.3;
}

.v2-credential__value {
  font-family: var(--font-headline);
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-bold);
  color: var(--color-navy-on);
  margin: 0;
  line-height: var(--lh-snug);
  word-break: break-word;
}

.v2-credential__value--amber {
  color: var(--color-amber);
}

.v2-credential__value--error {
  color: var(--color-error);
}

.v2-credential__member-of {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-3);
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px dashed rgba(255, 255, 255, 0.20);
  border-radius: var(--radius-md);
  text-align: center;
  min-width: 0;
}

.v2-credential__member-of-label {
  margin: 0;
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  color: var(--color-navy-on-soft);
  font-weight: var(--fw-semibold);
}

.v2-credential__member-of-logo {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-full);
  background-color: var(--color-surface);
  border: 2px solid var(--color-amber);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.v2-credential__member-of-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  padding: var(--space-1);
}

.v2-credential__member-of-initials {
  font-family: var(--font-headline);
  font-size: var(--fs-body-md);
  font-weight: var(--fw-bold);
  color: var(--color-navy-deep);
  line-height: 1;
}

.v2-credential__member-of-name {
  margin: 0;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-navy-on);
  line-height: var(--lh-snug);
  word-break: break-word;
  max-width: 100%;
}

@media (min-width: 768px) {
  .v2-credential__footer {
    flex-direction: row;
    align-items: stretch;
    gap: var(--space-5);
  }

  .v2-credential__footer-data {
    flex: 1 1 60%;
  }

  .v2-credential__member-of {
    flex: 0 0 auto;
    min-width: 132px;
    max-width: 160px;
  }
}
</style>
